import * as Schema from "effect/Schema";
import { OfflineWorldCopyReference } from "./offlineWorld.ts";

import { NonNegativeInt, TrimmedNonEmptyString } from "./baseSchemas.ts";
import { ContextSelectionExpression } from "./contextSelection.ts";

const Sha256Hex = Schema.String.check(Schema.isPattern(/^[0-9a-f]{64}$/u));

/**
 * Step 5 of the boundary repair order (O15-O17, O29-O30): the immutable
 * per-message boundary. Send freezes the active Agent session's exact
 * effective selection into one AgentContextResolution BEFORE execution
 * starts; a later rail edit reaches only a later message; and the rendered
 * in-set, provider-delivered context, and MCP-enforced set must be this one
 * resolution and digest (parity lands with the door at step 6).
 *
 * O29: this is deliberately its OWN schema, versioned
 * `agent-context-resolution-v1` — semantically distinct from the World's
 * MyWords `contextResolutions` (which compile Atom pairs for cleo-surface).
 * The Agent-message resolution is recorded World-side before provider
 * execution. The World door independently recomputes both its selection
 * receipt and this final resolution digest; the same immutable bytes then
 * ride the local command/event/provider pipeline and scoped MCP enforcement.
 */
export const AGENT_CONTEXT_RESOLUTION_SCHEMA_VERSION = "agent-context-resolution-v1";

/** Immutable persona admitted by the owning World, not access rights. */
export const LiveVoiceAgentSnapshot = Schema.Struct({
  agentKey: TrimmedNonEmptyString,
  revision: NonNegativeInt.check(Schema.isGreaterThan(0)),
  mode: Schema.Literals(["test", "live"]),
  activationRevision: Schema.optional(NonNegativeInt.check(Schema.isGreaterThan(0))),
  configDigest: Sha256Hex,
  name: TrimmedNonEmptyString.check(Schema.isMaxLength(80)),
  purpose: TrimmedNonEmptyString.check(Schema.isMaxLength(4000)),
  style: TrimmedNonEmptyString.check(Schema.isMaxLength(1000)),
  // A published GPT-Live voice id (lower-case name; the owning World's list is
  // the authority and refuses the rest). Not pinned to "marin": the World now
  // admits any published voice, and a readback must not refuse a legal one.
  voice: TrimmedNonEmptyString.check(Schema.isMaxLength(32)),
  backendMode: Schema.Literal("caller"),
  backendProvider: Schema.Null,
  backendModel: Schema.Null,
}).check(
  Schema.makeFilter(
    (value) =>
      (value.mode === "live"
        ? value.activationRevision !== undefined
        : value.activationRevision === undefined) ||
      "An active call requires its exact activation; a private test has none.",
  ),
);
export type LiveVoiceAgentSnapshot = typeof LiveVoiceAgentSnapshot.Type;
export const LiveVoiceMessageCall = Schema.Struct({
  requestKey: TrimmedNonEmptyString.check(Schema.isMaxLength(128)),
  delegationId: TrimmedNonEmptyString.check(Schema.isMaxLength(256)),
  callKey: TrimmedNonEmptyString.check(Schema.isMaxLength(512)),
  agent: LiveVoiceAgentSnapshot,
});
export type LiveVoiceMessageCall = typeof LiveVoiceMessageCall.Type;

export const AgentContextResolution = Schema.Struct({
  schemaVersion: Schema.Literal(AGENT_CONTEXT_RESOLUTION_SCHEMA_VERSION),
  /** Caller-minted identity; retries re-send the SAME id (one message, one resolution). */
  resolutionId: TrimmedNonEmptyString,
  /** SHA-256 hex over the canonical resolution body — see @cleo/shared/agentContextResolution. */
  digest: Sha256Hex,
  worldKey: TrimmedNonEmptyString,
  /** The room the addressed session belongs to (derived by the fold); null only for an ungoverned Local thread. */
  roomId: Schema.NullOr(TrimmedNonEmptyString),
  sessionId: TrimmedNonEmptyString,
  /** The exact receiver at freeze time (O28); null while the session is unbound. */
  agentEnvironmentId: Schema.NullOr(TrimmedNonEmptyString),
  agentThreadId: Schema.NullOr(TrimmedNonEmptyString),
  /** The World selection receipt this freeze was copied from. Arc SEL
   * (BD-CAN-14): the stored set carries no catalog claim, so the catalog
   * digests are optional legacy fields, absent from new freezes. */
  catalogDigest: Schema.optional(Sha256Hex),
  catalogCustodyDigest: Schema.optional(Sha256Hex),
  selectionDigest: Sha256Hex,
  selectionCut: NonNegativeInt,
  /** The frozen admitted member ids, in universe order. Empty is a real, typed selection. */
  memberIds: Schema.Array(TrimmedNonEmptyString),
  /** Bit i includes member i's content in the request. Other members remain
   * searchable references. Bound by this resolution and selection digests. */
  workingContextBitmap: Schema.optional(TrimmedNonEmptyString),
  expressions: Schema.optional(Schema.Array(ContextSelectionExpression)),
  /** Exact session tool choice at Send. It does not inject source content. */
  toolSelection: Schema.optional(
    Schema.Struct({
      revision: NonNegativeInt,
      toolNames: Schema.Array(TrimmedNonEmptyString),
    }),
  ),
  liveCall: Schema.optional(LiveVoiceMessageCall),
  frozenAt: TrimmedNonEmptyString,
});
export type AgentContextResolution = typeof AgentContextResolution.Type;

/** Local authority is the device's authenticated command/event store. It has
 * no World key, grant, or remote record. The existing message column retains
 * this receipt just as it retains a World receipt. */
export const LocalContextResolution = Schema.Struct({
  schemaVersion: Schema.Literal("local-context-resolution-v1"),
  /** Server-derived Reference reach, owned by this exact Local conversation. */
  createdMemberIds: Schema.optional(Schema.Array(TrimmedNonEmptyString)),
  resolutionId: TrimmedNonEmptyString,
  digest: Sha256Hex,
  worldKey: Schema.Null,
  roomId: Schema.Null,
  sessionId: TrimmedNonEmptyString,
  projectId: TrimmedNonEmptyString,
  agentEnvironmentId: TrimmedNonEmptyString,
  agentThreadId: TrimmedNonEmptyString,
  selectionDigest: Sha256Hex,
  selectionCut: NonNegativeInt,
  sourceResolutionId: Schema.optional(TrimmedNonEmptyString),
  offlineCopy: Schema.optional(OfflineWorldCopyReference),
  /** The local tool choice frozen with this message, not the next selection. */
  tools: Schema.optional(Schema.Array(TrimmedNonEmptyString)),
  expressions: Schema.Array(ContextSelectionExpression),
  memberIds: Schema.Array(TrimmedNonEmptyString),
  workingContextBitmap: Schema.optional(TrimmedNonEmptyString),
  frozenAt: TrimmedNonEmptyString,
});
export type LocalContextResolution = typeof LocalContextResolution.Type;
export const MessageContextResolution = Schema.Union([
  AgentContextResolution,
  LocalContextResolution,
]);
export type MessageContextResolution = typeof MessageContextResolution.Type;

/** Read-only evidence from before the room/session contract migration.
 * This is not a current selection or a usable provider boundary. */
export const HistoricalContextResolution = Schema.Struct({
  status: Schema.Literal("historical-unverified"),
  reason: Schema.Literal("pre-room-session-format"),
  resolutionId: TrimmedNonEmptyString,
  digest: Sha256Hex,
  rawJson: Schema.String,
});
export type HistoricalContextResolution = typeof HistoricalContextResolution.Type;

/** Exact World-door acknowledgement returned before a governed provider turn starts. */
export const AgentContextWorldRecordReceipt = Schema.Struct({
  recorded: Schema.Boolean,
  resolutionId: TrimmedNonEmptyString,
  digest: Sha256Hex,
});
export type AgentContextWorldRecordReceipt = typeof AgentContextWorldRecordReceipt.Type;

export const LocalContextRecordReceipt = Schema.Struct({
  recorded: Schema.Boolean,
  resolutionId: TrimmedNonEmptyString,
  digest: Sha256Hex,
  environmentId: TrimmedNonEmptyString,
  projectId: TrimmedNonEmptyString,
});
export type LocalContextRecordReceipt = typeof LocalContextRecordReceipt.Type;

/**
 * The provider-input leg retained until the matching turn settles. The digest
 * binds the complete frozen resolution stored on the user message; keeping the
 * receipt compact avoids copying a large member manifest into runtime logs.
 */
export const ProviderContextResolutionReceipt = Schema.Struct({
  turnId: TrimmedNonEmptyString,
  resolutionId: TrimmedNonEmptyString,
  digest: Sha256Hex,
  memberCount: NonNegativeInt,
  workingContext: Schema.optional(
    Schema.Struct({
      digest: Sha256Hex,
      documentCount: NonNegativeInt,
      bytes: NonNegativeInt,
    }),
  ),
  worldRecord: Schema.optional(AgentContextWorldRecordReceipt),
  localRecord: Schema.optional(LocalContextRecordReceipt),
}).check(
  Schema.makeFilter(
    (receipt) =>
      Boolean(receipt.worldRecord) !== Boolean(receipt.localRecord) ||
      "Exactly one authority receipt is required.",
  ),
);
export type ProviderContextResolutionReceipt = typeof ProviderContextResolutionReceipt.Type;
